import { create } from "zustand";

// This does NOT depend on whether the backend re-serves the design as a manifest layer (the single
// server `zones` layer is overwritten on every save + can't be client-deleted, which is why zones
// are tracked here too).
export type TargetKind = "points" | "zones";

export interface TargetSet {
  id: string;
  fieldId: string;
  kind: TargetKind;
  name: string;
  createdAt: string; // ISO
  count: number;
  ordered: boolean; // a work order was placed (vs Save-only)
  fc: GeoJSON.FeatureCollection;
}

const KEY = (fieldId: string) => `sm_target_designs:${fieldId}`;
const MAX = 20;

function load(fieldId: string): TargetSet[] {
  try {
    const raw = localStorage.getItem(KEY(fieldId));
    const arr = raw ? (JSON.parse(raw) as TargetSet[]) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function persist(fieldId: string, list: TargetSet[]) {
  try {
    localStorage.setItem(KEY(fieldId), JSON.stringify(list));
  } catch {
    /* quota / private mode */
  }
}

interface TargetSamplingState {
  byField: Record<string, TargetSet[]>;
  ensure: (fieldId: string) => void; // hydrate a field's sets from localStorage into reactive state (idempotent)
  add: (set: TargetSet) => void;
  remove: (fieldId: string, id: string) => void;
}

export const useTargetSampling = create<TargetSamplingState>((set, get) => ({
  byField: {},
  ensure: (fieldId) => {
    if (get().byField[fieldId]) return;
    set((s) => ({ byField: { ...s.byField, [fieldId]: load(fieldId) } }));
  },
  add: (item) =>
    set((s) => {
      const cur = s.byField[item.fieldId] ?? load(item.fieldId);
      const next = [item, ...cur.filter((x) => x.id !== item.id)].slice(0, MAX);
      persist(item.fieldId, next);
      return { byField: { ...s.byField, [item.fieldId]: next } };
    }),
  remove: (fieldId, id) =>
    set((s) => {
      const cur = s.byField[fieldId] ?? load(fieldId);
      const next = cur.filter((x) => x.id !== id);
      persist(fieldId, next);
      return { byField: { ...s.byField, [fieldId]: next } };
    }),
}));

// A SERVER-issued zone_version_id is a bare UUID; client-minted ids are `${fieldId}-${ts}-${seq}` —
// even with a UUID fieldId the suffix breaks the full-match, so the two shapes can't be confused.
const SERVER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Don't prune a set younger than this: a JUST-saved design's version can be missing from a still-cached
// SS8 list (staleTime 30s) — without the grace, saving zones would briefly delete the row it just made.
const PRUNE_GRACE_MS = 10 * 60 * 1000;

/*
 *
 * Callers must pass a list that actually CONTAINS zone events (proof the service lists them) — the
 * caller-side guard, not this function, is what keeps an older no-zone-events service from nuking
 * everything.
 */
export function pruneServerDeletedTargets(
  fieldId: string,
  liveVersionIds: ReadonlySet<string>,
): void {
  const st = useTargetSampling.getState();
  st.ensure(fieldId);
  const cur = useTargetSampling.getState().byField[fieldId] ?? [];
  const dead = cur.filter(
    (s) =>
      s.kind === "zones" &&
      SERVER_ID_RE.test(s.id) &&
      !liveVersionIds.has(s.id) &&
      Date.now() - (Date.parse(s.createdAt) || 0) > PRUNE_GRACE_MS,
  );
  for (const s of dead) st.remove(fieldId, s.id);
}

let seq = 0;
// Register a just-created/ordered design (POINTS or ZONES) so it shows as its own Target Sampling Layer.
// Returns the manifest LAYER id (`target-<kind>-<setId>`) so a Save-only caller can reveal it; '' when empty.
export function registerTarget(
  fieldId: string,
  kind: TargetKind,
  fc: GeoJSON.FeatureCollection,
  ordered: boolean,
  when: string,
  opts: { id?: string } = {},
): string {
  const count = fc.features?.length ?? 0;
  if (!count) return "";
  // include the creation timestamp so ids stay unique ACROSS reloads (the in-session `seq` resets
  // to 0 on reload, which would otherwise let a new design overwrite a persisted one with the same
  // id) a ZONE save's id is the SERVER's zone_version_id when the save returned one: the sidebar
  // now lists server zone versions as `target-zones-<version_id>`, so a matching id makes the
  // client copy and the server row ONE layer — same visibility state, no double listing — and the
  // server row simply takes over once the sample-events refetch lands.
  const id = opts.id || `${fieldId}-${Date.parse(when) || 0}-${++seq}`;
  const noun =
    kind === "points"
      ? `sampling point${count === 1 ? "" : "s"}`
      : `zone${count === 1 ? "" : "s"}`;
  useTargetSampling.getState().add({
    id,
    fieldId,
    kind,
    name: `${count} ${noun}${ordered ? "" : " (saved)"}`,
    createdAt: when,
    count,
    ordered,
    fc,
  });
  return `target-${kind}-${id}`;
}
